import { Link } from "react-router-dom";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import Logo from "../components/Logo";
import { ArrowLeft, MessageSquare, ShieldCheck } from "lucide-react";
import { DISPLAY_DOMAIN, SITE_URL } from "../lib/brand";

const SMSTermsPage = () => {
  return (
    <div className="min-h-screen bg-white text-slate-900">
      <div className="max-w-4xl mx-auto px-6 py-10">
        <Link to="/" className="inline-flex items-center gap-2 text-blue-700 hover:text-blue-900 text-sm mb-6">
          <ArrowLeft size={16} /> Back to Home
        </Link>

        <div className="flex items-center gap-3 mb-2">
          <Logo />
          <span className="text-xs uppercase tracking-widest text-slate-500">EchoSense Capital Club</span>
        </div>
        <h1 className="text-4xl font-serif font-semibold text-slate-900 mb-1 flex items-center gap-3">
          <MessageSquare size={28} className="text-blue-700" /> SMS Terms &amp; Opt-In Policy
        </h1>
        <p className="text-sm text-slate-500 mb-8">Last updated: February 2026</p>

        <Card className="bg-white border-slate-200">
          <CardContent className="p-6 md:p-8 space-y-6 text-slate-800 leading-relaxed text-[15px]">

            <section>
              <h2 className="text-xl font-semibold text-slate-900 mb-2">Program Description</h2>
              <p>
                <strong>EchoSense Capital Club SMS Program.</strong> When you provide your mobile phone number to
                EchoSense — either at registration, in your onboarding intake, by texting the published EchoSense
                number <strong>(833) 657-3746</strong>, or by checking an SMS-opt-in box in any EchoSense form — you
                consent to receive transactional and conversational SMS messages from EchoSense Capital Club.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-slate-900 mb-2">Types of Messages You May Receive</h2>
              <ul className="list-disc ml-6 space-y-1">
                <li>Account verification codes (one-time passwords).</li>
                <li>Onboarding status updates (KYC complete, accredited approved, partner handoff, etc.).</li>
                <li>Appointment scheduling and reminder confirmations (Microsoft Bookings).</li>
                <li>Compliance notices and red-alert / priority-review notifications.</li>
                <li>Reply messages from EchoSense staff in response to inbound texts.</li>
                <li>Security and fraud-prevention alerts.</li>
              </ul>
              <p className="mt-3">
                <strong>EchoSense does not send marketing or promotional SMS</strong> and does not share or sell your
                mobile number for marketing purposes.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-slate-900 mb-2">Message Frequency</h2>
              <p>
                Message frequency varies based on your activity in the portal. Most members receive fewer than 10
                messages per month; high-volume periods (active onboarding) may temporarily increase this.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-slate-900 mb-2">Cost</h2>
              <p>
                Message and data rates may apply. EchoSense does not charge for SMS, but your mobile carrier may
                charge you for messages received or sent. Contact your wireless provider for details on your plan.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-slate-900 mb-2">How to Opt In</h2>
              <p>You can opt in to receive SMS from EchoSense in any of the following ways:</p>
              <ul className="list-disc ml-6 mt-2 space-y-1">
                <li>Provide your mobile number during account registration on {DISPLAY_DOMAIN}.</li>
                <li>Provide or update your mobile number in the onboarding intake or portal Settings page.</li>
                <li>Text <strong>START</strong> or <strong>JOIN</strong> to <strong>(833) 657-3746</strong>.</li>
                <li>Verbally consent during a recorded phone call with EchoSense staff.</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-slate-900 mb-2">How to Opt Out</h2>
              <p>
                You may opt out of SMS at any time by replying <strong>STOP</strong>, <strong>END</strong>,
                <strong> CANCEL</strong>, <strong>UNSUBSCRIBE</strong>, or <strong>QUIT</strong> to any message we
                send you, or by texting any of those keywords to <strong>(833) 657-3746</strong>. You will receive a
                final confirmation message and no further SMS will be sent.
              </p>
              <p className="mt-3">
                Opting out of SMS does not affect your EchoSense membership or your ability to receive transactional
                emails. To opt out of email, contact{" "}
                <a href="mailto:admin@echosensecapital.com" className="text-blue-700 hover:underline">admin@echosensecapital.com</a>.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-slate-900 mb-2">Help &amp; Support</h2>
              <p>
                For assistance, reply <strong>HELP</strong> to any message, or contact us at:
              </p>
              <ul className="list-none mt-2 space-y-1">
                <li>📧 <a href="mailto:admin@echosensecapital.com" className="text-blue-700 hover:underline">admin@echosensecapital.com</a></li>
                <li>📞 <a href="tel:+18336573746" className="text-blue-700 hover:underline">+1 (833) 657-3746</a></li>
                <li>🌐 <a href={SITE_URL} className="text-blue-700 hover:underline">{DISPLAY_DOMAIN}</a></li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-slate-900 mb-2">Carrier Disclaimer</h2>
              <p>
                Carriers (AT&amp;T, Verizon, T-Mobile, US Cellular, Sprint, etc.) are not liable for delayed or
                undelivered messages. Supported carriers may change without notice.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-slate-900 mb-2">Mobile Information &amp; Third-Party Sharing</h2>
              <p>
                <strong>
                  No mobile information will be shared with third parties or affiliates for marketing or promotional
                  purposes.
                </strong>{" "}
                Information sharing to subcontractors in support services, such as customer service, identity
                verification (KYC), or compliance routing to an approved regulated partner, is permitted only with
                your written consent obtained through the portal. All such recipients are bound by confidentiality
                obligations. Mobile opt-in data and consent are <strong>not</strong> shared with any third parties.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-slate-900 mb-2">Privacy</h2>
              <p>
                For full details on how EchoSense collects, uses, and protects your information, see our{" "}
                <Link to="/privacy" className="text-blue-700 hover:underline">Privacy Policy</Link>.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-slate-900 mb-2">Changes to These Terms</h2>
              <p>
                EchoSense may update these SMS Terms from time to time. Material changes will be communicated by SMS
                or email. The "Last updated" date at the top reflects the most recent revision.
              </p>
            </section>

            <div className="pt-4 border-t border-slate-200 flex items-center gap-2 text-sm text-emerald-700">
              <ShieldCheck size={16} /> EchoSense Capital Club · Compliance-first SMS program
            </div>
          </CardContent>
        </Card>

        <div className="mt-8 text-center">
          <Button variant="outline" onClick={() => window.print()}>Print this Policy</Button>
        </div>
      </div>
    </div>
  );
};

export default SMSTermsPage;
